import marketplaceSellerIcon from "../../assets/images/icons/market_seller.svg";
import externalBusinessIcon from "../../assets/images/icons/external_business.svg";
import creatorBrandIcon from "../../assets/images/icons/creator.svg";
import objectiveProductIcon from "../../assets/images/icons/ads-objective-product.svg";
import objectiveProfileIcon from "../../assets/images/icons/ads-objective-profile.svg";
import objectiveMarketplaceIcon from "../../assets/images/icons/ads-objective-marketplace.svg";
import objectiveGoliveIcon from "../../assets/images/icons/ads-objective-golive.svg";
import objectiveSoundIcon from "../../assets/images/icons/ads-objective-sound.svg";
import ctaShopIcon from "../../assets/images/icons/ads-cta-shop.svg";
import ctaFollowIcon from "../../assets/images/icons/ads-cta-follow.svg";
import ctaLearnMoreIcon from "../../assets/images/icons/ads-cta-learn-more.svg";

// client-api's real CTA codes (configuration/constant.js `adCtas`): shop_now, view_listing,
// follow, visit_profile, watch_live, learn_more. Only 3 have an icon asset today - the rest
// render as text-only pills rather than guessing at an icon.
export const CTA_ICONS = {
  shop_now: ctaShopIcon,
  follow: ctaFollowIcon,
  learn_more: ctaLearnMoreIcon,
};

export const AD_OBJECTIVES = [
  {
    value: "product_listing_views",
    label: "Product Listing Views",
    description: "Show your product to more Shoppers and drive interest.",
    iconSrc: objectiveProductIcon,
  },
  {
    value: "profile_visits",
    label: "Profile Visits",
    description: "Increase Visits to yor store or brand profile.",
    iconSrc: objectiveProfileIcon,
  },
  {
    value: "marketplace_conversions",
    label: "Marketplace Conversions",
    description: "Get more purchases and add-to-card actions.",
    iconSrc: objectiveMarketplaceIcon,
  },
  {
    value: "go_live_promotions",
    label: "Go Live Promotion",
    description: "Boost awareness and attract more viewers to your live.",
    iconSrc: objectiveGoliveIcon,
  },
  // Only offered when the wizard is opened from an artist's "Promote this sound" button
  // (/ads/create?objective=sound_promotions&destination_type=track&destination_id=<track id>).
  {
    value: "sound_promotions",
    label: "Sound Promotion",
    description: "Get your sound heard by more listeners on Fan Home.",
    iconSrc: objectiveSoundIcon,
    soundOnly: true,
  },
];

// Reads the Promote this sound link. Returns the track id, or "" when the link is not one.
export function soundIdFromSearch(search) {
  const p = new URLSearchParams(search || "");
  if (p.get("objective") !== "sound_promotions" || p.get("destination_type") !== "track") return "";
  const id = p.get("destination_id") || "";
  return /^\d+$/.test(id) ? id : "";
}

// "profile" targets/destinations resolve to the logged-in user's own id automatically.
// "product"/"live"/"marketplace_listing" need a real target_id/destination_id - client-api
// only checks that the id exists (Product/LiveStream/User row, not deleted), it doesn't
// require a picker UI, so these are entered as a plain id field rather than a search-and-select.
export const SUPPORTED_TARGET_VALUES = ["profile", "product", "live", "marketplace_listing"];

export const AD_TARGET_TYPES = [
  { value: "profile", label: "Profile" },
  { value: "product", label: "Product" },
  { value: "live", label: "Go Live" },
];

export const AD_DESTINATION_TYPES = [
  { value: "profile", label: "Profile" },
  { value: "product", label: "Product" },
  { value: "marketplace_listing", label: "Marketplace Listing" },
  { value: "live", label: "Golive" },
  { value: "track", label: "Sound" },
];

// Mirrors client-api's configuration/constant.js `objectiveDestinationMap` - keep in sync
// if that map changes. Only "profile" is actually usable today (see SUPPORTED_TARGET_VALUES),
// so only the "profile_visits" objective can complete the wizard until product/live pickers exist.
export const OBJECTIVE_DESTINATION_MAP = {
  profile_visits: ["profile"],
  product_listing_views: ["product", "marketplace_listing"],
  marketplace_conversions: ["product", "marketplace_listing"],
  go_live_promotions: ["live"],
  sound_promotions: ["track"],
};

// Ad set targeting - matches client-api's configuration/ad-targeting.js real allowed values.
export const GENDERS = ["All", "Male", "Female", "Non-binary"];
export const DEVICES = ["All", "Android", "IOS", "Web"];
// Mirrors configuration/ad-targeting.js's AD_TARGETING_MIN_AGE/AD_TARGETING_MAX_AGE defaults
// (18/65) - the server rejects anything outside this range, so keep in sync if those env vars
// ever change. There's no API to fetch this range dynamically today.
export const AGE_MIN = 18;
export const AGE_MAX = 65;

export function formatInterestLabel(category) {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Seller-facing placement options (matches configuration/ad-targeting.js's
// search_result/marketplace_feed/category_results vocabulary, saved to AdSetTargeting.placements
// for display). `deliveryValue` is the separate vocabulary AdCreative.delivery_config.placements
// actually uses (configuration/ad-delivery.js) - the auction engine only reads deliveryValue.
// category_results -> "listing" is a judgment call (closest match to a category/listing page).
export const PLACEMENTS = [
  {
    key: "searchResult",
    value: "search_result",
    deliveryValue: "search",
    label: "Search Result",
    badge: "Most Popular",
    description: "Your Product can appear when buyers Search for products related to your listing.",
  },
  {
    key: "marketplaceFeed",
    value: "marketplace_feed",
    deliveryValue: "feed",
    label: "Marketplace Feed",
    description: "Your Product may appear in relevant market place",
  },
  {
    key: "categoryResults",
    value: "category_results",
    deliveryValue: "listing",
    label: "Category Results",
    description: "Display your product while buyers browse relevant product",
  },
];

export function daysBetween(startDate, endDate) {
  if (!startDate || !endDate) return null;
  return Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)));
}

export function formatSubmittedAt(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${datePart} • ${timePart}`;
}

export function getCurrentUserId() {
  try {
    const user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user"));
    return user?.id ?? null;
  } catch {
    return null;
  }
}

export const ADVERTISER_TYPES_FALLBACK = [
  {
    name: "Marketplace Seller",
    key: "marketplace_seller",
    description: "Set Products on the Marketplace",
    iconSrc: marketplaceSellerIcon,
  },
  {
    name: "External Business",
    key: "external_business",
    description: "Promote your business with Verified Profile",
    iconSrc: externalBusinessIcon,
  },
  {
    name: "Creator / Brand",
    key: "creator_brand",
    description: "Build your Brand and connect with Followers",
    iconSrc: creatorBrandIcon,
  },
];

export function getAdvertiserTypeIcon(code) {
  return ADVERTISER_TYPES_FALLBACK.find((t) => t.key === code)?.iconSrc;
}

// Ad (creative) lifecycle status
export const AD_STATUS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted for Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  ACTIVE: "Active",
  PAUSED: "Paused",
};

// Dashboard/list filter tabs map to Ad-level status, not Campaign-level status
// (AdCampaign only has Draft/Active/Paused/Completed - no review/rejected state).
export const AD_STATUS_FILTER_TABS = [
  { label: "All", value: null },
  { label: "Live", value: AD_STATUS.ACTIVE },
  { label: "In Review", value: AD_STATUS.SUBMITTED },
  { label: "Rejected", value: AD_STATUS.REJECTED },
];

// Active and Approved read as the same "Live" bucket for display purposes (badge label,
// badge/card color, and the Live filter tab) - Approved just hasn't been switched on yet,
// but visually it's not "in review" or "rejected" either.
export function getAdDisplayLabel(status) {
  if (status === AD_STATUS.ACTIVE || status === AD_STATUS.APPROVED) return "Live";
  if (status === AD_STATUS.SUBMITTED) return "In Review";
  if (status === AD_STATUS.REJECTED) return "Rejected";
  return status;
}

export function adMatchesFilterTab(status, tabValue) {
  if (tabValue === null) return true;
  if (tabValue === AD_STATUS.ACTIVE) return status === AD_STATUS.ACTIVE || status === AD_STATUS.APPROVED;
  return status === tabValue;
}

// Exact colors pulled from the Figma "Ad Creation & Review_Web" section (read-only access -
// design tokens: green_1 #ebf8ed, primary_green #095228, yellow_1 #fefdeb,
// primary_yellow #98911f, primery_red #74131c).
export function statusCardClasses(status) {
  switch (status) {
    case AD_STATUS.ACTIVE:
    case AD_STATUS.APPROVED:
      return "bg-[#ebf8ed] border-[#095228]";
    case AD_STATUS.SUBMITTED:
      return "bg-[#fefdeb] border-[#98911f]";
    case AD_STATUS.REJECTED:
      return "bg-[rgba(255,218,214,0.2)] border-[#74131c]";
    default:
      return "bg-white border-gray-200";
  }
}

export function statusBadgeClasses(status) {
  switch (status) {
    case AD_STATUS.ACTIVE:
    case AD_STATUS.APPROVED:
      return "bg-[#39b54a] text-white";
    case AD_STATUS.SUBMITTED:
      return "bg-[#ffdcbf] text-[#6a3b00]";
    case AD_STATUS.REJECTED:
      return "bg-[#ba1a1a] text-[#fefdeb]";
    case AD_STATUS.PAUSED:
      return "bg-gray-200 text-gray-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

// AdCampaign/AdSet use their own, separate status enum from AdCreative - just
// Draft/Active/Paused/Completed, none of the Submitted/Approved/Rejected review states (only
// the ad within a campaign/ad-set goes through admin moderation, not the campaign/ad-set
// itself). Both are created as Active immediately (see createCampaign/createAdSet call sites)
// since nothing in this UI ever transitions a campaign/ad-set out of Draft otherwise, and
// there's no pause/resume UI for campaigns/ad-sets yet - only for individual ads.
export function getCampaignDisplayLabel(status) {
  return status || "Draft";
}

export function campaignStatusBadgeClasses(status) {
  switch (status) {
    case "Active":
      return "bg-[#39b54a] text-white";
    case "Paused":
      return "bg-gray-200 text-gray-700";
    case "Completed":
      return "bg-[#c3c5d9] text-[#131b2e]";
    default: // Draft
      return "bg-[#ffdcbf] text-[#6a3b00]";
  }
}

// Campaign-level filter tabs, separate from AD_STATUS_FILTER_TABS - Campaign/AdSet only has
// Draft/Active/Paused/Completed, so unlike ads there's no Active+Approved merging or a
// Rejected bucket to account for.
export const CAMPAIGN_STATUS_FILTER_TABS = [
  { label: "All", value: null },
  { label: "Draft", value: "Draft" },
  { label: "Active", value: "Active" },
  { label: "Paused", value: "Paused" },
  { label: "Completed", value: "Completed" },
];

export function campaignMatchesFilterTab(status, tabValue) {
  if (tabValue === null) return true;
  return (status || "Draft") === tabValue;
}

// Card background/border per campaign status - same color language as statusCardClasses (ads)
// so campaign cards read consistently with the rest of the ads UI, not left plain white.
export function campaignCardClasses(status) {
  switch (status) {
    case "Active":
      return "bg-[#ebf8ed] border-[#095228]";
    case "Paused":
      return "bg-gray-100 border-gray-300";
    case "Completed":
      return "bg-[#EAEAF2] border-[#c3c5d9]";
    default: // Draft
      return "bg-[#fefdeb] border-[#98911f]";
  }
}
